<template>
    <v-container class="py-5 px-5" fluid v-if="this.$store.state.signedIn">
        <v-form ref="form" v-model="valid" lazy-validation>
                    <v-layout justify-center>
                        <v-flex xs12 class="px-2 py-2">
                            <v-toolbar dark flat color="secondary">
                                <v-toolbar-title :style="$store.state.textFormatting.headers">
                                    Dummy Data Injection
                                </v-toolbar-title>
                                <v-spacer />
                                <info>
                                    <template slot>
                                       Inject Dummy Data in to BGP Spotlight
                                    </template>
                                    <template slot="body">
                                        <p>
                                            This tool allows users to inject messages in to the system, to create events, in the same way that standard BGP messages do in our collectors. 
                                            Messages injected by users here create events which are visible only to the user who injected the message in the first place.
                                            They will also AUTOMATICALLY create an alert in your user (and send an email alert if you have USER emails enabled), which will be visible in your standard 'User Alerts' page.
                                        </p>
                                        <p>
                                            The scoring and way this message is treated in the analytics will be exactly the same as any other message. The only difference is we create an alert whether the system would or not.
                                        </p>
                                        <p> 
                                            Messages injected via these means also do not affect the 'state' of the system, in that anything you enter will not then create other events for other people.
                                        </p>
                                        <p>
                                            To create a message, do the following:
                                            <ul>
                                                <li> Choose a prefix for the message (including the CIDR). This can be based on a recent message you've seen or a prefix you are monitoring. </li>
                                                <li> Now, choose a collector to send the message to (and therefore into the event generation system FROM). This is effectively arbitrary, other than if you'd like to send many messages from multiple sources to see how it looks from more than one source.</li>
                                                <li> This will populate the list of peer IPs to send from, so go ahead and choose one of these. </li>
                                                <li> 
                                                    Once you choose the peer IP, this will automatically populate the peer ASN with the ASN this IP is peering on. 
                                                    You can then fill in the AS path for the message, which will begin with the peer ASN automatically put in.
                                                    The last number in this path will be the origin ASN for the event.
                                                </li>
                                                <li> Then, hit submit and wait for the event to appear in your 'User Alerts' page. </li>
                                            </ul>
                                        </p>
                                    </template>
                                </info>
                            </v-toolbar>
                            <v-card>
                                <v-card-text>
                                    <v-layout wrap>
                                        <v-flex xs12 class="px-2 py-2">
                                            <v-layout>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card flat>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Input a prefix for the message </h3>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex shrink class="px-2 py-3 text-xs-center">
                                                    <v-icon> arrow_forward </v-icon>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card flat>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Select a collector to send the injected message to</h3>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex shrink class="px-2 py-3 text-xs-center">
                                                    <v-icon> arrow_forward </v-icon>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card flat>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Then, choose an IP (from the chosen collector) to send the message from. This will automatically fill in the ASN to be sent from.</h3>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex shrink class="px-2 py-3 text-xs-center">
                                                    <v-icon> arrow_forward </v-icon>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card flat>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Finally, insert an AS path for the message (the last ASN will be the origin ASN in the message)</h3>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Prefix </h3>
                                                            <v-layout>
                                                                <v-flex xs12>
                                                                    <v-text-field :style="$store.state.textFormatting.main" v-model="form.prefix" :rules="pre_rules" label="Prefix" required> </v-text-field>
                                                                </v-flex >
                                                            </v-layout>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Collector</h3>
                                                            <v-layout>
                                                                <v-flex xs12 v-if="!collector_loading">
                                                                    <v-select :style="$store.state.textFormatting.main" @change="peerIPChoice" v-model="form.collector" item-text="long_name" item-value="name" :items="collector_selection" label="Collector" required> </v-select>
                                                                </v-flex>
                                                                <v-flex xs12 v-if="collector_loading">
                                                                    <v-progress-circular indeterminate color="secondary" > </v-progress-circular>
                                                                </v-flex>
                                                            </v-layout>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> Peer IP</h3>
                                                            <v-layout>
                                                                <v-flex xs12 v-if="!collector_loading">
                                                                    <v-select :style="$store.state.textFormatting.main" @change="formIPFill" v-model="form.peer_ip" item-text="peer_ip" item-value="peer_ip" :items="peer_ip_select" label="Collector" required> </v-select>
                                                                </v-flex>
                                                                <v-flex xs12 v-if="collector_loading">
                                                                    <v-progress-circular indeterminate color="secondary" > </v-progress-circular>
                                                                </v-flex>
                                                            </v-layout>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                                <v-flex xs6 md3 class="px-2">
                                                    <v-card>
                                                        <v-card-text>
                                                            <h3 :style="$store.state.textFormatting.main"> AS Path (for message)</h3>
                                                            <v-layout>
                                                                <v-flex xs12 v-if="!collector_loading">
                                                                    <v-layout>
                                                                        <v-flex shrink class="px-2" :style="$store.state.textFormatting.main" v-if="form.peer_asn"> <br/> {{ form.peer_asn }} </v-flex>
                                                                        <v-text-field :style="$store.state.textFormatting.main" v-model="form.as_path" :rules="as_path_rules" label="AS Path Rules" :disabled="!form.peer_asn" required> </v-text-field>
                                                                    </v-layout>
                                                                </v-flex>
                                                                <v-flex xs12 v-if="collector_loading">
                                                                    <v-progress-circular indeterminate color="secondary" > </v-progress-circular>
                                                                </v-flex>
                                                            </v-layout>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                            </v-layout>
                                               
                                        </v-flex>
                                        
                                        
                                    </v-layout>
                                    
                                    <v-layout>
                                        <v-flex shrink class="px-4">
                                            <v-btn :disabled="!valid||post_loading" @click="download" color="success" :style="$store.state.textFormatting.main"> SUBMIT </v-btn>
                                            <v-progress-circular indeterminate color="secondary" v-if="post_loading"> </v-progress-circular>
                                        </v-flex>
                                        <v-flex xs6 md3 class="px-2" v-if="post_msg">
                                            <v-card :color="post_msg_color">
                                                <v-card-text>
                                                    <h3 :style="$store.state.textFormatting.main"> {{ post_msg }}</h3>
                                                </v-card-text>
                                            </v-card>
                                        </v-flex>
                                    </v-layout>
                                </v-card-text>
                            </v-card>
                        </v-flex>
                    </v-layout>
        </v-form>
    </v-container>
</template>

<script>

import { API, graphqlOperation } from 'aws-amplify';
import info from "../../shared/operational/infopopout.vue";
import { findParentComponentByName } from '../../components/utils/findParent.js'


export default {
    name: 'dummydata',
    components: {
        info
    },
    data () {
        return {
            rules: [v => !!v || "Value required"],
            data: [],
            name: [{name: "name"}],
            slider: 0,
            id: 0,
            showUrls: false,
            loading: false,
            useFromDays: [],
            useFromMonths: [],
            links: [],
            valid: false,
            collector_selection: [],
            collector_selected: [],
            collector_loading: false,
            create_msg_col: 'success',
            create_msg: null,
            current_jobs: [],
            pre_delete_job: null,
            job_count: null,
            show_pre_delete: false,
            time_to_adj: false,
            time_from_adj: false,
            asn_rules: [ v => /(^\d+$)/.test(v) || 'ASN must be non-negative integer'],
            as_path_rules: [ v => /^(([\d]+)([\s]{1}))*([\d]+){1}$/.test(v) || 'AS path must be space separated list of positive integers'],
            showCollectors: false,
            pre_rules: [ v => /(^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([8-9]|[1-2][0-9]|3[0-2]))$|^s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:)))(%.+)?s*(\/([1-9]|[1-9][0-9]|1[0-1][0-9]|12[0-8]))$)/.test(v) || 'Address must be valid IPv4 or IPv6' ],
            form: {
                prefix: '',
                collector: null,
                peer_ip: null,
                peer_asn: null,
                as_path: null
            },
            peer_ip_select: [],
            collector_peerips: {},
            post_loading: false,
            post_msg: false,
            post_msg_color: 'success'
        }
    },
    beforeMount () {

        let app = findParentComponentByName(this, 'app')
        app.checkSignedIn()

    },
    mounted () {
        this.getCollectorNames();
        //this.getPeerIPs();
    },
    methods: {
        download: async function () {
            if (this.$refs.form.validate()) {
                this.sendDummyData()
            }
        },
        getCollectorNames: async function () {
            try {
                this.collector_loading = true;
                this.collector_selection = []
                var query = `
                    query getCollectorNames {
                        getCollectorNames {
                            name
                            collector_description
                        }
                    }
                `;
                console.log(query)
                const res = await API.graphql(graphqlOperation(query));
                console.log(res);
                if (res) {
                    if (res.data.getCollectorNames) {
                        var collectorNames = res.data.getCollectorNames;
                        if (collectorNames) {
                            if (collectorNames.length > 0) {
                                for (var i = 0 ; i < collectorNames.length ; i++ ) {
                                    if (collectorNames[i].name.includes("rrc")) {
                                        
                                        this.collector_selection.push({
                                            'name': collectorNames[i].name,
                                            'long_name': 'RIS Live ' + collectorNames[i].collector_description.replace(/["“”']+/g, '')
                                        })
                                    }
                                    
                                }
                                this.collector_selection.sort((a, b) => (a.name > b.name) ? 1 : -1)
                            }
                        }
                    }
                }
                this.collector_loading = false;
                this.getPeerIPs();
            } catch (err) {
                console.log(err)
                this.collector_loading = false;
            }

        },
        getPeerIPs: async function () {
            try {
                this.collector_loading = true;

                var query = `
                    query getPeerIPs {
                        getPeerIPs {
                            peerip
                            peerasn
                            collector
                        }
                    }
                `;
                console.log(query)
                const res = await API.graphql(graphqlOperation(query));
                console.log(res);
                if (res) {
                    if (res.data.getPeerIPs) {
                        var collector_names = []
                        for ( var i = 0 ; i < this.collector_selection.length ; i++ ) {
                            collector_names.push(this.collector_selection[i].name)
                        }
                        console.log(collector_names)
                        var peerIPData = res.data.getPeerIPs;
                        if (peerIPData) {
                            if (peerIPData.length > 0) {
                                for (var i = 0 ; i < peerIPData.length ; i++ ) {
                                    console.log(peerIPData[i].collector)
                                    if (collector_names.includes(peerIPData[i].collector)) {
                                        if (!(Object.keys(this.collector_peerips).includes(peerIPData[i].collector))) {
                                            this.collector_peerips[peerIPData[i].collector] = []
                                        }
                                        this.collector_peerips[peerIPData[i].collector].push({
                                            'peer_ip': peerIPData[i].peerip,
                                            'peer_asn': peerIPData[i].peerasn,
                                        })
                                    }
                                }
                                //this.collector_selection.sort((a, b) => (a.name > b.name) ? 1 : -1)
                            }
                        }
                    }
                }
                this.collector_loading = false;
            } catch (err) {
                console.log(err)
                this.collector_loading = false;
            }
        },
        peerIPChoice: async function () {
            if (this.form.collector) {
                console.log("selecting peer ips")
                this.peer_ip_select = this.collector_peerips[this.form.collector]
                this.form.peer_ip = false
                this.form.peer_asn = false
            } else {
                console.log("no collector selected")
                this.peer_ip_select = []
                this.form.peer_ip = false
                this.form.peer_asn = false
            }
        },
        formIPFill: async function () {
            for ( var i = 0 ; i < this.peer_ip_select.length ; i++ ) {
                if (this.peer_ip_select[i].peer_ip == this.form.peer_ip) {
                    this.form.peer_asn = this.peer_ip_select[i].peer_asn
                }
            }
        },
        sendDummyData: async function () {
            this.post_loading = true
            this.post_msg = false
            var ip_version = 4
            if (this.form.prefix.includes(":")) {
                ip_version = 6
            }

            try {
                var query = `
                    mutation injectDummyData {
                        injectDummyData (dummyData: {
                            message_type: A,
                            ip_version: ipv` + ip_version + `,
                            prefix: "` + this.form.prefix + `",
                            peer_ip: "` + this.form.peer_ip + `",
                            as_path: "` + this.form.peer_asn + ` ` + this.form.as_path + `",
                            collector: "` + this.form.collector + `"
                        }) {
                            status
                            error
                            errordesc
                            command
                            createResponse
                        }
                    }            
                `;
                console.log(query)
                const res = await API.graphql(graphqlOperation(query));
                console.log(res);
                this.post_msg = "Success, message injected"
                this.post_msg_color = "success"
                this.form.collector = false
                this.form.as_path = false
                this.form.peer_ip = false
                this.form.peer_asn = false
                this.post_loading = false
            } catch (err) {
                console.log(err)
                this.post_msg = "Error: " + err
                this.post_msg_color = "red"
                this.post_loading = false
            }

        },
        
    },
    watch: {
        /*form: {
            deep: true,
            handler() {
                if (this.form.collector) {
                    this.peerIPChoice()
                }
                
            }
        }*/
    }

}
</script>

<style scoped>
  
  .tile {
    background: white;
  }
</style>